import React from 'react';
import {Button, CircularProgress, Stack, Typography} from '@mui/material';
import {CTAButtonFixed, IconIllustration, AlertModal} from 'presentation/components';

import {INSTRUCTION_ITEMS} from '../../constants';
import IconNamesType from 'presentation/icons/IconNamesType';
import {
	AlreadyPreRegisteredCard,
	CustomAlert,
	FacialBiometricsHeader,
	GuardianSelection,
	InstructionItem,
	ResponsiveCard,
} from '../../components';
import {translations} from 'presentation/translations-new';
import {Navigate} from 'react-router-dom';
import {HomeEnum} from 'presentation/constants';
import {FacialBiometricsTaggingEnum} from 'main/analytics/gaTagsEnum';
import {useInitialInstructions} from './hooks/useInitialInstructions';

const {
	title,
	teaTitle,
	teaDescription,
	teaWarning,
	description,
	telehealthDescription,
	telehealthTitle,
	primaryButton,
	secondaryButton,
	firstInstruction,
	errorModal,
} = translations['pt-br'].others.facialBiometric.initialInstructions;

export function InitialInstructions() {
	const {
		isTablet,
		isAlreadyPreRegistered,
		isCheckingPreRegister,
		showGuardianSelection,
		showErrorModal,
		isFromTelehealthBooking,
		setShowErrorModal,
		handleNext,
		handleErrorModalClose,
		handleBackToHome,
		handleSelectGuardian,
		canRegister,
		hasFacialBiometrics,
		isUnder16,
		isTea,
	} = useInitialInstructions();

	if (hasFacialBiometrics && !canRegister) {
		return <Navigate to={HomeEnum.HOME_PAGE} replace />;
	}

	if (showGuardianSelection) {
		return <GuardianSelection onNext={handleSelectGuardian} />;
	}

	return (
		<>
			<FacialBiometricsHeader />
			<Stack alignItems="center" height="100%">
				{isAlreadyPreRegistered ? (
					<AlreadyPreRegisteredCard />
				) : (
					<>
						<ResponsiveCard>
							<Stack alignItems="center" gap={2}>
								<IconIllustration variant="primary" customIconName="face-focus" />
								<Typography variant="h6">
									{isFromTelehealthBooking
										? telehealthTitle
										: isTea
											? teaTitle
											: title}
								</Typography>
								<Typography variant="body1">
									{isFromTelehealthBooking ? telehealthDescription : description}
								</Typography>
								{isTea && (
									<Typography variant="body1" alignSelf="flex-start">
										{teaDescription}
									</Typography>
								)}
								<Stack justifyContent="flex-start" width="100%" gap={2} mt={1}>
									{INSTRUCTION_ITEMS.map((item, index) => (
										<InstructionItem
											key={index}
											title={
												index === 0 && isUnder16 && !isTea
													? firstInstruction.under16Title
													: item.title
											}
											description={item.description}
											iconName={item.iconName as IconNamesType}
										/>
									))}
								</Stack>
								{isTea && <CustomAlert message={teaWarning} />}
								{!isTablet && (
									<Stack direction="row" gap={1}>
										<Button
											onClick={handleBackToHome}
											variant="outlined"
											disabled={isCheckingPreRegister}
											data-tag-id={
												FacialBiometricsTaggingEnum.CLICK_CADASTRAR_BIOMETRIA_FACIAL_DEPOIS
											}>
											{secondaryButton}
										</Button>
										<Button
											onClick={handleNext}
											disabled={isCheckingPreRegister}
											endIcon={
												isCheckingPreRegister ? (
													<CircularProgress size={16} color="inherit" />
												) : null
											}
											data-tag-id={
												FacialBiometricsTaggingEnum.CLICK_CADASTRAR_BIOMETRIA_FACIAL_AGORA
											}>
											{primaryButton}
										</Button>
									</Stack>
								)}
							</Stack>
						</ResponsiveCard>
						{isTablet ? (
							<CTAButtonFixed.Root $isTablet={isTablet} sx={{bottom: 0}}>
								<Stack direction="row" gap={2}>
									<CTAButtonFixed.Action
										onClick={handleBackToHome}
										variant="outlined"
										fullWidth
										disabled={isCheckingPreRegister}
										data-tag-id={
											FacialBiometricsTaggingEnum.CLICK_CADASTRAR_BIOMETRIA_FACIAL_DEPOIS
										}>
										{secondaryButton}
									</CTAButtonFixed.Action>
									<CTAButtonFixed.Action
										onClick={handleNext}
										fullWidth
										disabled={isCheckingPreRegister}
										endIcon={
											isCheckingPreRegister ? (
												<CircularProgress size={16} color="inherit" />
											) : null
										}
										data-tag-id={
											FacialBiometricsTaggingEnum.CLICK_CADASTRAR_BIOMETRIA_FACIAL_AGORA
										}>
										{primaryButton}
									</CTAButtonFixed.Action>
								</Stack>
							</CTAButtonFixed.Root>
						) : null}
					</>
				)}
			</Stack>
			<AlertModal
				open={showErrorModal}
				onClose={() => setShowErrorModal(false)}
				variant="info"
				customIconName="info-filled"
				headerTitle={errorModal.headerTitle}
				title={errorModal.title}
				description={errorModal.description}
				buttonLabel={errorModal.button}
				footerContent={
					<Button onClick={handleErrorModalClose} variant="contained" fullWidth>
						{errorModal.button}
					</Button>
				}
			/>
		</>
	);
}
